import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Customer, LeadService } from "../api";
import {
  lead360DataErrorAction,
  lead360DataLoadedAction,
  requestLead360DataAction,
  resetLead360DataAction,
} from "../store/reducers/lead360";
import { State } from "../store/interfaces";

export const useLead360 = (leadService: LeadService) => {
  const lead360State = useSelector((state: State) => state.lead360);
  const dispatch = useDispatch();

  const _load = (customerId: string) => {
    dispatch(requestLead360DataAction({ customerId }));
    leadService
      .getCustomer360(customerId)
      .then((data) => {
        const sortedLeads = data.data?.leads.sort(
          (l1, l2) =>
            moment(l2.createdAt).toDate().getTime() -
            moment(l1.createdAt).toDate().getTime()
        );
        const currentSalesPerson = sortedLeads?.find(
          (lead) => lead.currentSalesPerson
        )?.currentSalesPerson;
        const currentCRMAgent = sortedLeads?.find(
          (lead) => lead.currentCRMAgent
        )?.currentCRMAgent;
        dispatch(
          lead360DataLoadedAction({
            customerId,
            data: data.data,
            currentSalesPerson,
            currentCRMAgent,
          })
        );
      })
      .catch((e) =>
        dispatch(
          lead360DataErrorAction({
            customerId,
            error: e,
          })
        )
      );
  };

  const _loadCompany = (customerId: string) => {
    dispatch(requestLead360DataAction({ customerId }));
    leadService
      .getCompany360(customerId)
      .then((data) => {
        const sortedLeads = data.data?.leads.sort(
          (l1, l2) =>
            moment(l2.createdAt).toDate().getTime() -
            moment(l1.createdAt).toDate().getTime()
        );
        const currentSalesPerson = sortedLeads?.find(
          (lead) => lead.currentSalesPerson
        )?.currentSalesPerson;
        const currentCRMAgent = sortedLeads?.find(
          (lead) => lead.currentCRMAgent
        )?.currentCRMAgent;
        dispatch(
          lead360DataLoadedAction({
            customerId,
            data: data.data,
            currentSalesPerson,
            currentCRMAgent,
          })
        );
      })
      .catch((e) =>
        dispatch(
          lead360DataErrorAction({
            customerId,
            error: e,
          })
        )
      );
  };

  const reset = () => {
    dispatch(resetLead360DataAction());
  };

  const load = (customerId: string) => {
    if (lead360State.customerId === customerId) {
      return;
    }
    _load(customerId);
  };

  const reload = () => {   
    if (!lead360State.customerId) {
      return;
    }
    _load(lead360State.customerId);
  };

  const loadCompany = (customerId: string) => {
    if (lead360State.customerId === customerId) {
      return;
    }
    _loadCompany(customerId);
  };

  const reloadCompany = () => {
    if (!lead360State.customerId) {
      return;
    }
    _loadCompany(lead360State.customerId);
  };

  const getInvalidCustomerFieldsForAutolineSync = (customer: Customer) => {
    const invalidFields = [];
    if (!customer?.addresses?.length) {
      invalidFields.push({
        displayName: "Address",
        group: "contact-details",
        type: "customer",
        value: "addresses",
      });
    }
    if (!customer?.prefix) {
      // invalidFields.push('Title');
      invalidFields.push({
        displayName: "Title",
        group: "contact-details",
        type: "customer",
        value: "prefix",
      });
    }
    if (!customer?.firstName) {
      // invalidFields.push('First Name');
      invalidFields.push({
        displayName: "First Name",
        group: "contact-details",
        type: "customer",
        value: "firstName",
      });
    }
    if (!customer?.middleName) {
      // invalidFields.push('Middle Name');
      invalidFields.push({
        displayName: "Middle Name",
        group: "contact-details",
        type: "customer",
        value: "middleName",
      });
    }
    if (!customer?.lastName) {
      // invalidFields.push('Last Name');
      invalidFields.push({
        displayName: "Last Name",
        group: "contact-details",
        type: "customer",
        value: "lastName",
      });
    }
    if (!customer?.gender) {
      // invalidFields.push('Gender');
      invalidFields.push({
        displayName: "Gender",
        group: "demographics",
        type: "customer",
        value: "gender",
      });
    }
    if (!customer?.primaryPhone) {
      // invalidFields.push('Phone');
      invalidFields.push({
        displayName: "Phone",
        group: "contact-details",
        type: "customer",
        value: "primaryPhone",
      });
    }
    if (!customer?.dob) {
      // invalidFields.push('DOB');
      const fieldName = customer?.isCompanyCustomer
        ? "Registration Date"
        : "DOB";
      invalidFields.push({
        displayName: fieldName,
        group: "demographics",
        type: "customer",
        value: "dob",
      });
    }

    return invalidFields;
  };

  return {
    ...lead360State,
    load,
    reload,
    reset,
    getInvalidCustomerFieldsForAutolineSync,
    loadCompany,
    reloadCompany,
  };
};
